import React from "react";
import PropTypes from "prop-types";
import fields from "../../json/VehicleBookingFields.json";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormSelect from "Common/Custom/FormSelect";
import FormInput from "Common/Custom/FormInput";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import Switch from "Common/Custom/Switch";
import FormSubmitButton from "Common/Custom/FormSubmitButton";

const AddVehicleBookingForm = ({
    values,
    errors,
    flights,
    handleChange,
    handleSubmit,
    loading,
    driverOptions,
    vehicleOptions,
    flightOptions,
    bookingOptions,
}) => {
    const { formdata } = fields;

    // Resolve dropdown options per field name
    const getOptions = (fieldName, fieldOptions) => {
        if (fieldName === "booked_id") return bookingOptions;
        if (fieldName === "vehicle_id") return vehicleOptions;
        if (fieldName === "flight_id") return flightOptions;
        if (fieldName === "driver_id") return driverOptions;
        if (fieldName === "transportation_type") return fieldOptions || [];
        return [];
    };

    // Render the correct input component based on field type
    const renderField = (field) => {
        if (field.type === "select") {
            return (
                <FormSelect
                    key={field.name}
                    field={field}
                    options={getOptions(field.name, field.options)}
                    value={values[field.name]}
                    handleChange={handleChange}
                    error={errors[field.name]}
                    disabled={field.disabled}
                    required={field.required}
                />
            );
        }

        if (field.type === "datetime-local") {
            return (
                <FormDatePicker
                    key={field.name}
                    field={field}
                    value={values[field.name]}
                    handleChange={handleChange}
                    error={errors[field.name]}
                    mode="datetime"
                    required={field.required}
                />
            );
        }

        if (field.type === "switch") {
            return (
                <Switch
                    key={field.name}
                    field={field}
                    values={values[field.name]}
                    handleChange={handleChange}
                    ValueOne="1"
                    ValueTwo="0"
                    LabelOne="Yes"
                    LabelTwo="No"
                    required={field.required}
                />
            );
        }

        // Default: text / number input
        // Cost field is disabled and not required when is_free is toggled on
        const isCostDisabled = field.name === "cost" && Number(values.is_free) === 1;
        return (
            <FormInput
                key={field.name}
                field={{
                    ...field,
                    disabled: field.disabled || isCostDisabled,
                    required: field.required && !isCostDisabled,
                }}
                value={values[field.name] ?? ""}
                handleChange={handleChange}
                error={errors[field.name]}
            />
        );
    };

    // Find the selected flight object for the info banner
    const selectedFlight = flights.find(f => Number(f.flight_id) === Number(values.flight_id));

    return (
        <PageBody
            headerchildren={<HeadingOne>Create Vehicle Booking</HeadingOne>}
            onSubmit={handleSubmit}
        >
            {/* Flight reference info banner — shown only when a flight is selected */}
            {selectedFlight && (
                <div className="col-12">
                    <div
                        className="alert border-0 shadow-sm mb-0"
                        style={{
                            backgroundColor: "rgba(var(--primary-rgb), 0.1)",
                            color: "var(--primary-color)",
                        }}
                    >
                        <h6 className="mb-2">
                            <i className="bi bi-airplane me-2" />
                            Flight Reference: {selectedFlight.flight_number}
                        </h6>
                        <div className="row g-3">
                            <div className="col-md-3">
                                <div className="small opacity-75">Departure</div>
                                <div className="fw-bold">{selectedFlight.departure}</div>
                            </div>
                            <div className="col-md-3">
                                <div className="small opacity-75">Arrival</div>
                                <div className="fw-bold">{selectedFlight.arrival}</div>
                            </div>
                            {selectedFlight.fly_from && (
                                <div className="col-md-3">
                                    <div className="small opacity-75">From</div>
                                    <div className="fw-bold">{selectedFlight.fly_from}</div>
                                </div>
                            )}
                            {selectedFlight.fly_to && (
                                <div className="col-md-3">
                                    <div className="small opacity-75">To</div>
                                    <div className="fw-bold">{selectedFlight.fly_to}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Form fields */}
            <div className="row g-3">
                {formdata.map(renderField)}
            </div>

            <FormSubmitButton
                label="Create"
                loading={loading}
                colSize="col-12"
                align="end"
            />
        </PageBody>
    );
};

AddVehicleBookingForm.propTypes = {
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    flights: PropTypes.array.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    driverOptions: PropTypes.array.isRequired,
    vehicleOptions: PropTypes.array.isRequired,
    flightOptions: PropTypes.array.isRequired,
    bookingOptions: PropTypes.array.isRequired,
};

export default AddVehicleBookingForm;
